import React from 'react';
import { FormField } from './components/FormField';
import { ProgressBar } from './components/ProgressBar';
import { useFormStore } from './store/formStore';
import { ChevronLeft, ChevronRight, FileText, Search, Shield, Lock, Copy, Check, AlertCircle, Save, Star, Award, Home, Building2, Scale, Clock, ArrowRight, CheckCircle, Users, Sparkles } from 'lucide-react';
import { generateDocument, GenerationProgress } from './services/documentGenerator';
import { documentTypeConfig } from './config/documentConfigs';
import { ReviewLease } from './components/ReviewLease';
import { AgreementDisplay } from './components/AgreementDisplay';
import { getDocumentTypeName } from './utils/documentHelpers';
import { GeneratingLoader } from './components/GeneratingLoader';
import { isFieldConditionallyRequired } from './utils/conditionalValidation';
import { DocumentReviewModal } from './components/DocumentReviewModal';
import { saveDocumentGeneration, getSessionId } from './services/documentStorage';
import { useEmbed } from './contexts/EmbedContext';
import { DownloadButton } from './components/DownloadButton';
import { PropertySelector } from './components/PropertySelector';
import { documentSaveService } from './services/documentSaveService';
import { userContextService } from './services/userContextService';
import { ConfirmationPage } from './components/ConfirmationPage';
import { EnhancedConfirmationPage } from './components/EnhancedConfirmationPage';
import { uploadDocumentToParent, fallbackToLocalStorage } from './services/parentAppCallback';
import { documentConverter } from './services/documentConverter';
import { ServiceCheckoutPage } from './components/ServiceCheckoutPage';
import { PaidDocumentUpload } from './components/PaidDocumentUpload';
import { CompletedDocumentPage } from './components/CompletedDocumentPage';
import { verifyPayment, type ServiceType } from './services/serviceOrderService';
import type { Annotation } from './services/tenancyReviewRewrite';

function App() {
  const { currentStep, currentSection, sections, responses, nextStep, previousStep, documentType, setDocumentType } = useFormStore();
  const { config: embedConfig, sendMessage, userContext, saveDocumentToParent } = useEmbed();
  const [agreement, setAgreement] = React.useState<string>('');
  const [documentContent, setDocumentContent] = React.useState<string | null>(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  type ViewState = 'landing' | 'generate' | 'review' | 'confirmation'
    | 'tenancy-checkout' | 'commercial-checkout'
    | 'tenancy-upload' | 'commercial-upload'
    | 'tenancy-complete' | 'commercial-complete';
  const [view, setView] = React.useState<ViewState>(() => {
    const params = new URLSearchParams(window.location.search);
    const svc = params.get('service');
    if (params.get('order_id') && params.get('session_id')) {
      if (svc === 'tenancy_review_rewrite') return 'tenancy-checkout';
      if (svc === 'commercial_lease_review') return 'commercial-checkout';
      const pending = localStorage.getItem('pending_service_order');
      if (pending) {
        try {
          const { serviceType } = JSON.parse(pending);
          if (serviceType === 'tenancy_review_rewrite') return 'tenancy-checkout';
          if (serviceType === 'commercial_lease_review') return 'commercial-checkout';
        } catch { /* fall through */ }
      }
      return 'landing';
    }
    if (svc === 'tenancy_review_rewrite') return 'tenancy-checkout';
    if (svc === 'commercial_lease_review') return 'commercial-checkout';
    return 'landing';
  });
  const [saveStatus, setSaveStatus] = React.useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [parentDocumentId, setParentDocumentId] = React.useState<string | null>(null);
  const [copiedText, setCopiedText] = React.useState(false);
  const [selectedDocType, setSelectedDocType] = React.useState<string>('');
  const [documentGenerationId, setDocumentGenerationId] = React.useState<string | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = React.useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = React.useState<string | undefined>(undefined);
  const [generationProgress, setGenerationProgress] = React.useState<GenerationProgress | null>(null);
  const [pdfSignedUrl, setPdfSignedUrl] = React.useState<string | null>(null);
  const [serviceOrderId, setServiceOrderId] = React.useState<string | null>(null);
  const [completedResult, setCompletedResult] = React.useState<{
    rewrittenDocument: string;
    annotations: Annotation[];
    analysis: Record<string, unknown>;
    originalText: string;
    negotiationSummary?: string;
  } | null>(null);

  const handleCopyAgreement = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const currentSectionData = sections[currentSection];

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [view]);

  React.useEffect(() => {
    const pending = localStorage.getItem('pending_service_order');
    if (pending) {
      try {
        JSON.parse(pending);
      } catch {
        localStorage.removeItem('pending_service_order');
      }
    }
  }, []);

  const handleProceedToSave = async () => {
    setSaveStatus('idle');
    setSaveError(null);

    if (!documentContent) {
      setError('No document available to save.');
      return;
    }

    if (!embedConfig.isEmbed) {
      if (!documentGenerationId) {
        setDocumentGenerationId(`local-${Date.now()}`);
      }
      setView('confirmation');
      sendMessage('navigationChange', { view: 'confirmation' });
      return;
    }

    if (!documentGenerationId) {
      setError('Document has not been generated yet. Please generate a document first.');
      return;
    }

    setSaveStatus('saving');
    setSaveError(null);

    try {
      const documentTitle = getDocumentTypeName(documentType || 'unknown');

      const pdfBlob = await documentConverter.convertToPDF(documentContent, documentTitle);

      const uploadPayload = {
        documentId: documentGenerationId,
        documentType: documentType || 'unknown',
        documentTitle,
        pdfBlob,
        metadata: {
          propertyAddress: responses['property-address'] as string,
          generatedAt: new Date().toISOString(),
          tier: 'premium',
          formData: responses,
        },
      };

      const result = await uploadDocumentToParent(uploadPayload);

      if (result.pdfSignedUrl) {
        setPdfSignedUrl(result.pdfSignedUrl);
      }

      if (result.success && result.parentDocumentId) {
        setParentDocumentId(result.parentDocumentId);
        setSaveStatus('success');
        setView('confirmation');
        sendMessage('navigationChange', { view: 'confirmation' });
      } else {
        await fallbackToLocalStorage(documentGenerationId, result.error || 'Unknown error');
        setSaveStatus('success');
        setView('confirmation');
        sendMessage('navigationChange', { view: 'confirmation' });
      }
    } catch (err) {
      console.error('Error saving document to parent:', err);
      await fallbackToLocalStorage(
        documentGenerationId,
        err instanceof Error ? err.message : 'Unknown error'
      );
      setSaveStatus('success');
      setView('confirmation');
      sendMessage('navigationChange', { view: 'confirmation' });
    }
  };

  const isDirectServiceLink = !!embedConfig.directService;

  const handleServiceBack = () => {
    if (isDirectServiceLink && embedConfig.isEmbed) {
      sendMessage('navigateBack', {});
    } else {
      setView('landing');
    }
  };

  if (view === 'tenancy-checkout' || view === 'commercial-checkout') {
    const svcType: ServiceType = view === 'tenancy-checkout' ? 'tenancy_review_rewrite' : 'commercial_lease_review';
    return (
      <ServiceCheckoutPage
        serviceType={svcType}
        onBack={handleServiceBack}
        onPaymentSuccess={(orderId) => {
          setServiceOrderId(orderId);
          const uploadView: ViewState = svcType === 'tenancy_review_rewrite' ? 'tenancy-upload' : 'commercial-upload';
          setView(uploadView);
          (async () => {
            try {
              await verifyPayment(orderId);
            } catch {
              // Payment verification is best-effort; user already completed Stripe checkout
            }
          })();
        }}
      />
    );
  }

  if (view === 'tenancy-upload' || view === 'commercial-upload') {
    const svcType: ServiceType = view === 'tenancy-upload' ? 'tenancy_review_rewrite' : 'commercial_lease_review';
    return (
      <PaidDocumentUpload
        serviceType={svcType}
        orderId={serviceOrderId || ''}
        onBack={handleServiceBack}
        hideBackButton={embedConfig.isEmbed}
        onComplete={(result) => {
          setCompletedResult(result);
          setView(svcType === 'tenancy_review_rewrite' ? 'tenancy-complete' : 'commercial-complete');
        }}
      />
    );
  }

  if (view === 'tenancy-complete' || view === 'commercial-complete') {
    const svcType: ServiceType = view === 'tenancy-complete' ? 'tenancy_review_rewrite' : 'commercial_lease_review';
    if (!completedResult) {
      setView('landing');
      return null;
    }
    return (
      <CompletedDocumentPage
        serviceType={svcType}
        rewrittenDocument={completedResult.rewrittenDocument}
        annotations={completedResult.annotations}
        analysis={completedResult.analysis}
        originalText={completedResult.originalText}
        negotiationSummary={completedResult.negotiationSummary}
        orderId={serviceOrderId || ''}
        hideBackButton={embedConfig.isEmbed}
        onBack={() => {
          setCompletedResult(null);
          setServiceOrderId(null);
          handleServiceBack();
        }}
      />
    );
  }

  if (isGenerating) {
    return (
      <GeneratingLoader
        documentType={documentType ? getDocumentTypeName(documentType) : undefined}
        generationProgress={generationProgress}
      />
    );
  }

  if (view === 'confirmation') {
    const handleDownload = async () => {
      if (documentContent) {
        try {
          await documentConverter.downloadDocument({
            documentContent,
            documentTitle: `${getDocumentTypeName(documentType || 'unknown')} - Premium`,
            documentType: documentType || 'unknown',
            format: 'pdf'
          });
        } catch (error) {
          console.error('Error downloading document:', error);
          setError('Failed to download document. Please try again.');
        }
      }
    };

    return (
      <EnhancedConfirmationPage
        documentType={documentType || 'unknown'}
        documentGenerationId={documentGenerationId || 'N/A'}
        documentContent={documentContent || ''}
        timestamp={new Date().toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
        unlockedTiers={['premium']}
        parentDocumentId={parentDocumentId}
        pdfSignedUrl={pdfSignedUrl}
        selectedTier="premium"
        isEmbed={embedConfig.isEmbed}
        onGenerateAnother={() => {
          setView('landing');
          setDocumentContent(null);
          setAgreement('');
          setDocumentGenerationId(null);
          setSaveStatus('idle');
          setSaveError(null);
          setParentDocumentId(null);
          setPdfSignedUrl(null);
          setGenerationProgress(null);
          sendMessage('navigationChange', { view: 'landing' });
        }}
        onViewInParent={() => {
          if (embedConfig.isEmbed) {
            sendMessage('navigateToDocuments', { documentId: parentDocumentId });
          }
        }}
        onDownload={handleDownload}
      />
    );
  }

  if (view === 'review') {
    return <ReviewLease
      onBack={() => {
        setView('landing');
        sendMessage('navigationChange', { view: 'landing' });
      }}
    />;
  }

  if (view === 'landing') {
    return (
      <div className="min-h-screen relative overflow-hidden bg-white">
        <div className="relative z-20 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-black rounded-2xl mb-8">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-5xl sm:text-6xl font-semibold mb-6 leading-tight tracking-tight">
              <span className="text-gray-900">Landlord & Tenant.</span>
              <br />
              <span className="text-gray-400">Simplified.</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed font-normal">
              Create or review legally compliant property and legal documents with our intelligent, AI-powered platform.
            </p>

            {/* Reform Notice Banner */}
            <div className="mb-8 max-w-3xl mx-auto bg-gray-900 rounded-2xl p-6 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-white rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-gray-900 text-sm font-bold">!</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">2025 Renters' Rights Bill - Important Changes</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    From May 2026, all new tenancies will be periodic (rolling monthly) by default. Section 21 'no-fault' evictions are abolished.
                    We recommend using <span className="font-semibold text-white">Periodic Tenancy Agreements</span> now to ensure compliance and avoid future complications.
                  </p>
                </div>
              </div>
            </div>

            {/* Document Type Selector */}
            <div className="mb-12 max-w-xl mx-auto">
              <label className="block text-sm font-medium text-gray-500 mb-3 tracking-wide">
                SELECT DOCUMENT TYPE
              </label>
              <select
                value={selectedDocType}
                onChange={(e) => setSelectedDocType(e.target.value)}
                className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 text-base"
              >
                <option value="" className="text-gray-900">Choose a document type...</option>
                <optgroup label="Tenancy Agreements" className="text-gray-900">
                  <option value="periodic-tenancy" className="text-gray-900">Periodic Tenancy Agreement (RECOMMENDED - 2025 Reform Compliant)</option>
                  <option value="standard-residential" className="text-gray-900">Standard Residential Tenancy Agreement</option>
                  <option value="commercial-lease" className="text-gray-900">Commercial Lease Agreement</option>
                  <option value="student-accommodation" className="text-gray-900">Student Accommodation Agreement</option>
                </optgroup>
                <optgroup label="Reform Transition Documents" className="text-gray-900">
                  <option value="tenancy-conversion-pack" className="text-gray-900">Fixed-Term to Periodic Conversion Pack (2025 Reform)</option>
                </optgroup>
                <optgroup label="Property Management" className="text-gray-900">
                  <option value="deposit-protection" className="text-gray-900">Tenancy Deposit Protection Notice</option>
                  <option value="rent-increase" className="text-gray-900">Rent Increase Notice</option>
                  <option value="written-statement-prescribed-terms" className="text-gray-900">Written Statement of Prescribed Terms</option>
                  <option value="property-inspection" className="text-gray-900">Property Inspection Checklist</option>
                  <option value="inventory-report" className="text-gray-900">Inventory and Condition Report</option>
                  <option value="right-to-rent" className="text-gray-900">Right to Rent Checklist</option>
                </optgroup>
                <optgroup label="Mediation & Dispute Resolution" className="text-gray-900">
                  <option value="mediation-agreement" className="text-gray-900">Mediation Agreement Template</option>
                  <option value="dispute-framework" className="text-gray-900">Dispute Resolution Framework</option>
                  <option value="mediation-request" className="text-gray-900">Mediation Request Form</option>
                  <option value="evidence-collection" className="text-gray-900">Evidence Collection Checklist</option>
                  <option value="communication-log" className="text-gray-900">Communication Log Template</option>
                  <option value="settlement-agreement" className="text-gray-900">Settlement Agreement Template</option>
                  <option value="mediation-guide" className="text-gray-900">Mediation Process Guide</option>
                  <option value="dispute-assessment" className="text-gray-900">Dispute Category Assessment</option>
                </optgroup>
                <optgroup label="Legal Notices & Court Documents" className="text-gray-900">
                  <option value="section-13-notice" className="text-gray-900">Section 13 Rent Increase Notice (2025 Compliant)</option>
                  <option value="section8-ground8" className="text-gray-900">Section 8 Notice - Ground 8 (Serious Rent Arrears)</option>
                  <option value="section8-ground14" className="text-gray-900">Section 8 Notice - Ground 14 (Anti-social Behaviour)</option>
                  <option value="possession-claim" className="text-gray-900">Possession Claim Form N5</option>
                  <option value="witness-statement" className="text-gray-900">Witness Statement Template</option>
                  <option value="rent-arrears-schedule" className="text-gray-900">Rent Arrears Schedule</option>
                  <option value="section8-assessment" className="text-gray-900">Section 8 Grounds Assessment Tool</option>
                  <option value="pre-action-protocol" className="text-gray-900">Pre-Action Protocol Checklist</option>
                  <option value="court-bundle" className="text-gray-900">Court Bundle Template</option>
                </optgroup>
              </select>
            </div>

            <div className="flex items-center justify-center space-x-8 text-sm text-gray-500 font-medium">
              <div className="flex items-center">
                <div className="w-1.5 h-1.5 bg-gray-900 rounded-full mr-2"></div>
                2025 Compliant
              </div>
              <div className="flex items-center">
                <div className="w-1.5 h-1.5 bg-gray-900 rounded-full mr-2"></div>
                AI-Powered
              </div>
              <div className="flex items-center">
                <div className="w-1.5 h-1.5 bg-gray-900 rounded-full mr-2"></div>
                Legally Binding
              </div>
            </div>
          </div>

          {/* Main Action Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-20">
            {/* Generate Agreement Option */}
            <div
              onClick={() => {
                if (!selectedDocType) {
                  alert('Please select a document type first');
                  return;
                }
                setDocumentType(selectedDocType);
                setView('generate');
                sendMessage('navigationChange', { view: 'generate' });
              }}
              className="group relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 border border-gray-200 hover:border-gray-300 transition-all duration-300 cursor-pointer"
            >
              <div className="relative z-10">
                <div className="flex items-center justify-center w-12 h-12 bg-gray-900 rounded-xl mb-6 mx-auto group-hover:bg-black transition-colors">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 text-center mb-3">Write Agreement</h2>
                {selectedDocType ? (
                  <div className="mb-4">
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-2 shadow-sm">
                      <p className="text-xs text-gray-500 text-center font-medium mb-1">SELECTED DOCUMENT</p>
                      <p className="text-gray-900 text-center font-medium text-sm">
                        {getDocumentTypeName(selectedDocType)}
                      </p>
                    </div>
                  </div>
                ) : null}
                <p className="text-gray-600 text-center leading-relaxed">
                  {selectedDocType ? 'Click to start writing your agreement' : 'Select a document type above to begin'}
                </p>
                <div className="mt-6 flex justify-center">
                  <div className="inline-flex items-center text-gray-900 font-medium group-hover:text-black transition-colors">
                    <span className="text-sm">Start Writing</span>
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>

            {/* Analyse Agreement Option */}
            <div
              onClick={() => {
                setView('review');
                sendMessage('navigationChange', { view: 'review' });
              }}
              className="group relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 border border-gray-200 hover:border-gray-300 transition-all duration-300 cursor-pointer"
            >
              <div className="relative z-10">
                <div className="flex items-center justify-center w-12 h-12 bg-gray-900 rounded-xl mb-6 mx-auto group-hover:bg-black transition-colors">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 text-center mb-3">Analyse Agreement</h2>
                <p className="text-gray-600 text-center leading-relaxed">
                  Upload and analyse an existing legal document with AI-powered legal analysis and compliance checking
                </p>
                <div className="mt-6 flex justify-center">
                  <div className="inline-flex items-center text-gray-900 font-medium group-hover:text-black transition-colors">
                    <span className="text-sm">Upload & Analyse</span>
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Fixed Fee Services */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-100 rounded-full text-xs font-semibold text-gray-600 uppercase tracking-wider mb-5">
                <Scale className="w-3.5 h-3.5" />
                Professional Services
              </div>
              <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-4">Fixed Fee Document Review</h2>
              <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
                Expert AI-powered review and rewrite with detailed compliance analysis, annotated changes, and legal references for every modification.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Tenancy Agreement Card */}
              <div
                onClick={() => setView('tenancy-checkout')}
                className="group relative bg-white rounded-3xl border border-gray-200 overflow-hidden hover:shadow-elevated-lg hover:border-gray-300 transition-all duration-500 cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80&auto=format&fit=crop"
                    alt="Residential property"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-gray-900/50 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/95 backdrop-blur-sm rounded-full text-xs font-bold text-gray-900">
                      <Star className="w-3 h-3 fill-gray-900 text-gray-900" />
                      Most Popular
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/30">
                        <Home className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white leading-tight">Tenancy Agreement</h3>
                        <p className="text-white/70 text-xs">Review & Re-Write</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-white">&pound;15.50</p>
                      <p className="text-white/60 text-xs">one-time fee</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 pb-5">
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    Full compliance review against the Renters' Rights Bill 2025 with a complete rewrite and annotated changes for every clause.
                  </p>

                  <div className="space-y-2.5 mb-6">
                    {[
                      'Compliance analysis against 8+ Acts of Parliament',
                      'Complete AI-powered document rewrite',
                      'Line-by-line annotated changes with legal references',
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <CheckCircle className="w-4 h-4 text-gray-900 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Results in minutes</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <FileText className="w-3.5 h-3.5" />
                      <span>PDF, Word & Text</span>
                    </div>
                    <div className="flex-1" />
                    <div className="inline-flex items-center gap-1 text-gray-900 font-semibold text-sm group-hover:gap-2 transition-all">
                      Get Started
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Commercial Lease Card */}
              <div
                onClick={() => setView('commercial-checkout')}
                className="group relative bg-white rounded-3xl border border-gray-200 overflow-hidden hover:shadow-elevated-lg hover:border-gray-300 transition-all duration-500 cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80&auto=format&fit=crop"
                    alt="Commercial office"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-gray-900/50 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/95 backdrop-blur-sm rounded-full text-xs font-bold text-gray-900">
                      <Sparkles className="w-3 h-3 text-gray-900" />
                      Expert Analysis
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/30">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white leading-tight">Commercial Lease</h3>
                        <p className="text-white/70 text-xs">Comparative Review</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-white">&pound;18.75</p>
                      <p className="text-white/60 text-xs">one-time fee</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 pb-5">
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    Upload two lease versions for AI-powered clause-by-clause comparison with plain-English analysis and negotiation recommendations.
                  </p>

                  <div className="space-y-2.5 mb-6">
                    {[
                      'Clause-by-clause comparison of both documents',
                      'Plain-English analysis of every difference',
                      'Negotiation recommendations and strategy',
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <CheckCircle className="w-4 h-4 text-gray-900 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Results in minutes</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <Scale className="w-3.5 h-3.5" />
                      <span>Risk assessment</span>
                    </div>
                    <div className="flex-1" />
                    <div className="inline-flex items-center gap-1 text-gray-900 font-semibold text-sm group-hover:gap-2 transition-all">
                      Get Started
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social proof bar */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 py-5 px-6 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Users className="w-4 h-4 text-gray-400" />
                <span>Trusted by property professionals across the UK</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-gray-900 text-gray-900" />
                ))}
                <span className="text-sm font-medium text-gray-700 ml-1">4.9/5 average rating</span>
              </div>
            </div>
          </div>

          {/* Security and Compliance Information */}
          <div className="bg-white rounded-3xl border border-gray-200 p-12 shadow-sm">
            <h3 className="text-2xl font-semibold text-gray-900 mb-10 text-center">
              Security & Legal Compliance
            </h3>
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-xl mb-5 mx-auto">
                  <Lock className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900 text-base">End-to-End Encryption</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">All data is encrypted in transit and at rest with military-grade security</p>
                </div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-xl mb-5 mx-auto">
                  <FileText className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900 text-base">2025 Legal Updates</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">Incorporates latest legal changes and regulatory requirements</p>
                </div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-xl mb-5 mx-auto">
                  <Shield className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900 text-base">AI-Powered Compliance</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">Advanced AI ensures every document meets current legal standards</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-20 space-y-8">
            <p className="text-gray-500 text-sm font-medium">
              Trusted by thousands of property professionals and legal practitioners across the UK
            </p>

            {/* Legal Disclaimer */}
            <div className="bg-gray-50 rounded-3xl border border-gray-200 p-8 max-w-4xl mx-auto">
              <h4 className="text-base font-semibold text-gray-900 mb-3">Legal Disclaimer</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                We are not a regulated law firm and do not provide legal representation. Our services are advisory only, offering guidance and step-by-step support to help you navigate and complete legal processes while representing yourself. Always consult a licensed attorney for personalized legal advice.
              </p>
            </div>

          </div>
        </div>
        </div>
      </div>
    );
  }

  const documentConfig = documentType && documentTypeConfig[documentType];
  const useCurrentSection = sections[currentSection];

  if (!useCurrentSection) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Form Error</h2>
          <p className="mt-2 text-gray-600">Unable to load the form section. Please try again.</p>
        </div>
      </div>
    );
  }

  const currentField = useCurrentSection.fields[currentStep];
  const isLastStep = currentStep === useCurrentSection.fields.length - 1 &&
                     !currentField?.nextStep?.conditions &&
                     !currentField?.nextStep?.default;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const isRequired = currentField.validation?.conditionalRequired
      ? isFieldConditionallyRequired(currentField, responses)
      : currentField.validation?.required ?? false;

    if (!responses[currentField.id] && isRequired) {
      setError('This field is required');
      return;
    }

    if (!isLastStep) {
      nextStep();
    } else {
      setIsGenerating(true);
      try {
        if (!documentType) {
          throw new Error('No document type selected');
        }

        if (!documentTypeConfig[documentType]) {
          throw new Error(`Unsupported document type: ${documentType}`);
        }

        const result = await generateDocument({
          documentType,
          formData: responses,
          tier: 'premium',
          onProgress: (progress) => {
            setGenerationProgress(progress);
          }
        });

        setDocumentContent(result.content);
        setAgreement(result.content);

        try {
          const savedDoc = await saveDocumentGeneration({
            documentType,
            formData: responses,
            premiumContent: result.content,
            userEmail: responses.landlordEmail as string || responses.tenantEmail as string,
            cacheHit: result.cacheHit,
            generationTimeMs: result.generationTimeMs,
            metadata: {
              tokenCount: result.tokenCount,
              costEstimate: result.costEstimate,
            },
          });
          setDocumentGenerationId(savedDoc.id);

          const userId = userContextService.getUserIdOrSession(userContext);
          const subscriptionTier = userContext?.subscriptionTier || 'free';

          if (embedConfig.isEmbed && saveDocumentToParent) {
            try {
              const syncResult = await saveDocumentToParent({
                documentGenerationId: savedDoc.id,
                documentType,
                propertyId: selectedPropertyId,
                standardContent: result.content,
                formData: responses,
                subscriptionTier,
              });

              if (syncResult.success) {
                await documentSaveService.updateSyncStatus(
                  savedDoc.id,
                  'synced',
                  syncResult.parentDocumentId
                );
              }
            } catch (syncError) {
              console.error('Failed to sync with parent app:', syncError);
            }
          }

          sendMessage('documentGenerated', {
            documentType,
            documentId: savedDoc.id,
            tier: 'premium',
            cacheHit: result.cacheHit,
            generationTimeMs: result.generationTimeMs,
          });
        } catch (saveError) {
          console.error('Failed to save document:', saveError);
        }
      } catch (error) {
        console.error('Error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to generate agreement. Please try again.';
        setError(errorMessage);

        sendMessage('error', {
          message: errorMessage,
          code: 'GENERATION_ERROR',
        });
      } finally {
        setIsGenerating(false);
        setGenerationProgress(null);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 lg:py-12">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200">
          <div className="p-6 sm:p-8 lg:p-12 border-b border-gray-100 rounded-t-2xl">
            <div className="flex items-center justify-between mb-6">
              {!embedConfig.isEmbed && (
                <button
                  onClick={() => {
                    setView('landing');
                    setAgreement('');
                    setDocumentContent(null);
                    setError(null);
                    setSelectedDocType('');
                    setDocumentType('');
                    setSaveStatus('idle');
                    setSaveError(null);
                    sendMessage('navigationChange', { view: 'landing' });
                  }}
                  className="text-sm text-gray-500 hover:text-gray-900 flex items-center transition-colors group"
                >
                  <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
                  Back to Home
                </button>
              )}

              <div className="flex items-center space-x-2">
                <div className="inline-flex items-center justify-center w-8 h-8 bg-black rounded-lg">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-semibold text-gray-900">Rentfix</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
                {useCurrentSection.title}
              </h1>

              {!agreement && (
                <button
                  type="submit"
                  form="main-form"
                  className="inline-flex items-center px-4 py-2 sm:px-5 sm:py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-black transition-all duration-200 shadow-sm hover:shadow"
                >
                  {isLastStep ? (
                    isGenerating ? 'Writing...' : 'Write Agreement'
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1.5" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          <form id="main-form" onSubmit={handleSubmit}>
            {!agreement && (
              <>
                {embedConfig.isEmbed && currentStep === 0 && currentSection === 'initial' && (
                  <div className="px-6 sm:px-8 lg:px-12 pt-6">
                    <PropertySelector
                      selectedPropertyId={selectedPropertyId}
                      onPropertySelect={setSelectedPropertyId}
                      className="mb-6"
                    />
                  </div>
                )}
                <div className="px-6 sm:px-8 lg:px-12">
                  {currentField && <FormField field={currentField} onSkip={nextStep} />}
                </div>

                <div className="px-6 sm:px-8 lg:px-12 py-8">
                  <ProgressBar />

                  {error && (
                    <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <div>
                        <p className="text-red-800 font-medium">Error</p>
                        <p className="text-red-600 text-sm">{error}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-6 sm:px-8 lg:px-12 py-5 border-t border-gray-200 bg-gray-50/50 rounded-b-2xl">
                  <button
                    type="button"
                    onClick={previousStep}
                    disabled={currentStep === 0 && currentSection === 'initial'}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1.5" />
                    Previous
                  </button>

                  <div className="hidden sm:block text-xs text-gray-400">
                    Press Enter to continue
                  </div>

                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-black transition-all duration-200 shadow-sm"
                  >
                    {isLastStep ? (
                      isGenerating ? 'Writing...' : 'Write Agreement'
                    ) : (
                      <>
                        Next
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            {documentContent && (
              <div className="w-full">
                {/* Premium Document Header */}
                <div className="relative overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 px-8 py-8">
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.15) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)'
                      }} />
                    </div>
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                          <Star className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h2 className="text-2xl font-bold text-white">
                              {documentType ? getDocumentTypeName(documentType) : 'Document'}
                            </h2>
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-white text-gray-900">
                              <Award className="w-3 h-3" />
                              PREMIUM
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm">Professional-grade, court-ready legal document</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Bar */}
                <div className="bg-gray-50 border-b border-gray-200 px-8 py-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleCopyAgreement(documentContent)}
                      className="inline-flex items-center px-4 py-2.5 border border-gray-200 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200"
                    >
                      {copiedText ? (
                        <>
                          <Check className="h-4 w-4 mr-2 text-gray-900" />
                          <span className="text-gray-900">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Text
                        </>
                      )}
                    </button>
                    <DownloadButton
                      content={documentContent}
                      documentType={documentType || 'document'}
                      documentTitle={getDocumentTypeName(documentType)}
                      documentTier="premium"
                      documentGenerationId={documentGenerationId || undefined}
                      userId={userContextService.getUserIdOrSession(userContext)}
                    />

                    <div className="flex-1" />

                    <button
                      type="button"
                      onClick={handleProceedToSave}
                      disabled={saveStatus === 'saving'}
                      className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg hover:from-gray-800 hover:to-gray-700 text-sm font-semibold transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saveStatus === 'saving' ? (
                        <>
                          <Save className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          Proceed
                          <ChevronRight className="w-4 h-4 ml-1.5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Document Content */}
                <div className="px-8 py-8 max-h-[700px] overflow-y-auto bg-white">
                  <AgreementDisplay content={documentContent} />
                </div>

                {saveStatus === 'error' && saveError && (
                  <div className="mx-8 mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-red-800 font-medium mb-2">
                          Failed to Save Document
                        </p>
                        <p className="text-sm text-red-700">
                          {saveError}
                        </p>
                        <button
                          type="button"
                          onClick={handleProceedToSave}
                          className="mt-3 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-800"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>

      {documentContent && (
        <DocumentReviewModal
          isOpen={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          documentContent={documentContent}
          documentType={documentType || 'tenancy-agreement'}
          documentGenerationId={documentGenerationId || undefined}
          onApplyChanges={(updatedContent) => {
            setDocumentContent(updatedContent);
            setAgreement(updatedContent);
          }}
        />
      )}
    </div>
  );
}

export default App;
